import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef } from 'react';
export function AcEnvelopeMeta(props) {
    const disabled = props.disabled === true;
    return (_jsxs("div", { className: "ac-envelope-meta", role: "group", "aria-label": "Envelope shape", children: [_jsx(PipRow, { rowLabel: 'Sustain ▸', ariaLabel: "Sustain segment", total: props.totalSegments, active: props.sustainSegment, maxEnabled: props.endSegment, onChange: props.onSustainChange, disabled: disabled }), _jsx(PipRow, { rowLabel: 'End ▣', ariaLabel: "Envelope length", total: props.totalSegments, active: props.endSegment, maxEnabled: props.totalSegments, onChange: props.onEndChange, disabled: disabled })] }));
}
function PipRow(props) {
    const { total, active, maxEnabled, onChange, disabled: rowDisabled } = props;
    const pipRefs = useRef([]);
    const firstEnabled = useCallback(() => {
        for (let i = 1; i <= total; i += 1) {
            if (i <= maxEnabled) {
                return i;
            }
        }
        return 1;
    }, [total, maxEnabled]);
    const lastEnabled = useCallback(() => {
        for (let i = total; i >= 1; i -= 1) {
            if (i <= maxEnabled) {
                return i;
            }
        }
        return 1;
    }, [total, maxEnabled]);
    const nextEnabled = useCallback((from, direction) => {
        // Walk enabled pips with wrap-around. Bounded at `total` steps to
        // guarantee termination even if `maxEnabled` is unusually small.
        let candidate = from;
        for (let step = 0; step < total; step += 1) {
            candidate += direction;
            if (candidate < 1) {
                candidate = total;
            }
            else if (candidate > total) {
                candidate = 1;
            }
            if (candidate <= maxEnabled) {
                return candidate;
            }
        }
        return from;
    }, [total, maxEnabled]);
    const focusPip = useCallback((index) => {
        const el = pipRefs.current[index - 1];
        if (el !== null && el !== undefined) {
            el.focus();
        }
    }, []);
    const handleKeyDown = useCallback((event, index) => {
        if (rowDisabled) {
            return;
        }
        if (onChange === undefined) {
            return;
        }
        const disabled = index > maxEnabled;
        switch (event.key) {
            case ' ':
            case 'Enter': {
                if (disabled) {
                    return;
                }
                event.preventDefault();
                onChange(index);
                return;
            }
            case 'ArrowRight':
            case 'ArrowDown': {
                event.preventDefault();
                const target = nextEnabled(index, 1);
                onChange(target);
                focusPip(target);
                return;
            }
            case 'ArrowLeft':
            case 'ArrowUp': {
                event.preventDefault();
                const target = nextEnabled(index, -1);
                onChange(target);
                focusPip(target);
                return;
            }
            case 'Home': {
                event.preventDefault();
                const target = firstEnabled();
                onChange(target);
                focusPip(target);
                return;
            }
            case 'End': {
                event.preventDefault();
                const target = lastEnabled();
                onChange(target);
                focusPip(target);
                return;
            }
            default:
                return;
        }
    }, [rowDisabled, onChange, maxEnabled, nextEnabled, firstEnabled, lastEnabled, focusPip]);
    // Roving tabindex: exactly one enabled pip carries `tabIndex={0}` so
    // keyboard users have a single tab stop per radio group. The chosen pip
    // is the active one if it is enabled; otherwise the first enabled pip.
    const activeIsEnabled = active >= 1 && active <= maxEnabled;
    const tabStop = activeIsEnabled ? active : firstEnabled();
    const pips = [];
    for (let i = 1; i <= total; i += 1) {
        const isActive = i === active;
        const pipDisabled = rowDisabled || i > maxEnabled;
        const handleClick = onChange === undefined || pipDisabled
            ? undefined
            : () => onChange(i);
        pips.push(_jsx("span", { ref: (el) => {
                pipRefs.current[i - 1] = el;
            }, className: "ac-envelope-meta__pip", role: "radio", "aria-checked": isActive, "aria-disabled": pipDisabled ? true : undefined, "data-active": isActive ? 'true' : undefined, "data-disabled": pipDisabled ? 'true' : undefined, onClick: handleClick, onKeyDown: (event) => handleKeyDown(event, i), tabIndex: !rowDisabled && i === tabStop && i <= maxEnabled ? 0 : -1, children: i }, i));
    }
    return (_jsxs("div", { className: "ac-envelope-meta__control", "data-disabled-row": rowDisabled ? 'true' : undefined, children: [_jsx("span", { className: "ac-envelope-meta__label", children: props.rowLabel }), _jsx("div", { className: "ac-envelope-meta__pips", role: "radiogroup", "aria-label": props.ariaLabel, "aria-disabled": rowDisabled ? true : undefined, children: pips })] }));
}
//# sourceMappingURL=AcEnvelopeMeta.js.map